// KnowledgeDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { BookOpen, FileText, Upload, Plus, Trash2, CheckCircle, Bot } from 'lucide-react';
import { KNOWLEDGE_CATEGORIES } from '../constants';

/**
 * Dialog for adding/editing knowledge base entries
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.formData - Current form data
 * @param {function} props.onFormChange - Callback when form data changes
 * @param {Object|null} props.editingKnowledge - Knowledge entry being edited (null for new)
 * @param {Array} props.agents - List of available AI agents
 * @param {function} props.onSave - Callback when save is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const KnowledgeDialog = ({
  open,
  onOpenChange,
  formData,
  onFormChange,
  editingKnowledge,
  agents = [],
  onSave,
  onCancel
}) => {
  const handleAddEntry = () => {
    onFormChange({
      ...formData,
      entries: [...formData.entries, { title: '', content: '' }]
    });
  };

  const handleRemoveEntry = (idx) => {
    const newEntries = formData.entries.filter((_, i) => i !== idx);
    onFormChange({
      ...formData,
      entries: newEntries.length ? newEntries : [{ title: '', content: '' }]
    });
  };

  const handleEntryChange = (idx, field, value) => {
    const newEntries = [...formData.entries];
    newEntries[idx][field] = value;
    onFormChange({ ...formData, entries: newEntries });
  };

  const toggleAgent = (agentId) => {
    const currentAgents = formData.assigned_agents || [];
    const newAgents = currentAgents.includes(agentId)
      ? currentAgents.filter(id => id !== agentId)
      : [...currentAgents, agentId];
    onFormChange({ ...formData, assigned_agents: newAgents });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            {editingKnowledge ? 'Edit Knowledge Entry' : 'Add Knowledge Entry'}
          </DialogTitle>
          <DialogDescription>
            Add documents or rich text entries to the AI knowledge base
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title || ''}
                onChange={(e) => onFormChange({ ...formData, title: e.target.value })}
                placeholder="e.g., Product Pricing Guidelines"
                data-testid="knowledge-title"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select 
                value={formData.category || 'general'} 
                onValueChange={(val) => onFormChange({ ...formData, category: val })}
              >
                <SelectTrigger data-testid="knowledge-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KNOWLEDGE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Source Type</Label>
            <div className="flex gap-4 mt-2">
              <Button
                variant={formData.source_type === 'manual' ? 'default' : 'outline'}
                size="sm"
                onClick={() => onFormChange({ ...formData, source_type: 'manual' })}
                data-testid="knowledge-source-manual"
              >
                <FileText className="h-4 w-4 mr-2" />
                Rich Text
              </Button>
              <Button
                variant={formData.source_type === 'upload' ? 'default' : 'outline'}
                size="sm"
                onClick={() => onFormChange({ ...formData, source_type: 'upload' })}
                data-testid="knowledge-source-upload"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Document
              </Button>
            </div>
          </div>

          {formData.source_type === 'upload' ? (
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground mb-2">Drag and drop or click to upload</p>
              <p className="text-xs text-muted-foreground">Supports PDF, DOCX, TXT (max 10MB)</p>
              <Input type="file" className="mt-4" accept=".pdf,.docx,.txt,.md" data-testid="knowledge-file-input" />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Main Content</Label>
                <Textarea
                  value={formData.content || ''}
                  onChange={(e) => onFormChange({ ...formData, content: e.target.value })}
                  placeholder="Enter the main knowledge content... (supports markdown)"
                  rows={4}
                  data-testid="knowledge-content"
                />
              </div>

              {/* Sub-entries */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Sub-Entries</Label>
                  <Button variant="outline" size="sm" onClick={handleAddEntry} data-testid="knowledge-add-entry">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Entry
                  </Button>
                </div>
                <div className="space-y-3">
                  {formData.entries?.map((entry, idx) => (
                    <div key={idx} className="p-3 border rounded-lg bg-slate-50 space-y-2" data-testid={`knowledge-entry-${idx}`}>
                      <div className="flex items-center gap-2">
                        <Input
                          value={entry.title || ''}
                          onChange={(e) => handleEntryChange(idx, 'title', e.target.value)}
                          placeholder={`Entry ${idx + 1} title`}
                          className="flex-1"
                        />
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveEntry(idx)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      <Textarea
                        value={entry.content || ''}
                        onChange={(e) => handleEntryChange(idx, 'content', e.target.value)}
                        placeholder="Entry content..."
                        rows={2}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Assign to Agents */}
          <div>
            <Label className="flex items-center gap-2">
              <Bot className="h-4 w-4" />
              Assign to AI Agents
            </Label>
            <p className="text-xs text-muted-foreground mb-2">Select which AI agents have access to this knowledge</p>
            <div className="flex flex-wrap gap-2">
              {agents.map(agent => (
                <Badge
                  key={agent.id}
                  variant={formData.assigned_agents?.includes(agent.id) ? 'default' : 'outline'}
                  className={`cursor-pointer ${formData.assigned_agents?.includes(agent.id) ? 'bg-violet-500' : ''}`}
                  onClick={() => toggleAgent(agent.id)}
                  data-testid={`knowledge-agent-${agent.id}`}
                >
                  {agent.name}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="knowledge-cancel">Cancel</Button>
          <Button onClick={onSave} data-testid="knowledge-save">
            <CheckCircle className="h-4 w-4 mr-2" />
            {editingKnowledge ? 'Update Entry' : 'Add Entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KnowledgeDialog;
